<template>
  <div class="apagar-workbench">
    <div class="workbench-header">
      <div>
        <b-button variant="link" class="back-btn" @click="$router.push('/movimentos/contasapagar/listagem')">
          <i class="fa fa-arrow-left"></i>
        </b-button>
        <div>
          <h3>Painel Analitico do Contas a Pagar</h3>
          <span>Analise, explore fornecedores e baixe titulos sem voltar para a listagem</span>
        </div>
      </div>
      <div class="header-actions">
        <b-form-input v-model="busca" placeholder="Buscar documento, fornecedor ou grupo..." />
        <b-button variant="light" pill :disabled="loading" @click="carregar">
          <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': loading }"></i> Atualizar
        </b-button>
      </div>
    </div>

    <b-overlay :show="loading" rounded="sm">
      <div class="kpi-grid">
        <button v-for="card in kpis" :key="card.label" type="button" class="kpi-card" :class="card.class" @click="aplicarAtalho(card.filter)">
          <small>{{ card.label }}</small>
          <strong>{{ card.value }}</strong>
          <span>{{ card.hint }}</span>
          <i :class="card.icon"></i>
        </button>
      </div>

      <div class="workbench-grid">
        <section class="panel panel-wide">
          <div class="panel-head">
            <div>
              <strong>Fornecedores</strong>
              <span>Clique para abrir os titulos do fornecedor</span>
            </div>
            <b-badge variant="info" pill>{{ fornecedores.length }}</b-badge>
          </div>
          <div class="supplier-board">
            <button v-for="f in fornecedores" :key="f.COD_FORNEC" type="button" class="supplier-card" :class="{ active: filtro.tipo === 'fornecedor' && filtro.valor === f.COD_FORNEC }" @click="filtrar('fornecedor', f.COD_FORNEC, f.NOME_FORNECEDOR)">
              <span>{{ f.NOME_FORNECEDOR }}</span>
              <strong>{{ formatBRL(f.TOTAL_ABERTO) }}</strong>
              <em>{{ f.TITULOS }} titulos | {{ formatBRL(f.TOTAL_ATRASADO) }} atraso</em>
              <b-progress :value="percent(f.TOTAL_ABERTO, maxFornecedor)" max="100" height="7px" variant="info" />
            </button>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <div>
              <strong>Risco da carteira</strong>
              <span>Clique no grafico para filtrar</span>
            </div>
          </div>
          <div class="chart-wrap">
            <canvas ref="chartFaixas"></canvas>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <div>
              <strong>Grupos de despesa</strong>
              <span>Origem dos compromissos</span>
            </div>
          </div>
          <div class="chart-wrap">
            <canvas ref="chartGrupos"></canvas>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <div>
              <strong>Baixado hoje</strong>
              <span>Movimento do dia</span>
            </div>
          </div>
          <div class="today-paid">
            <strong>{{ formatBRL(baixadoHoje.TOTAL) }}</strong>
            <span>{{ baixadoHoje.TITULOS || 0 }} pagamentos efetuados</span>
          </div>
          <div class="recent-list">
            <div v-for="p in ultimasBaixas.slice(0, 7)" :key="p.SEQUENCIA" class="recent-row">
              <div>
                <strong>{{ p.DOCUMENTO }} / {{ p.PARCELAS }}</strong>
                <span>{{ p.NOME_FORNECEDOR }}</span>
              </div>
              <em>{{ formatBRL(p.VL_PAGO) }}</em>
            </div>
          </div>
        </section>
      </div>

      <section class="panel table-panel">
        <div class="panel-head">
          <div>
            <strong>Titulos em foco</strong>
            <span>{{ filtroLabel }} | {{ titulosFiltrados.length }} registros | {{ formatBRL(totalFiltrado) }}</span>
          </div>
          <div class="d-flex align-items-center">
            <b-button v-if="filtro.tipo" variant="outline-secondary" pill size="sm" class="mr-2" @click="limparFiltro">
              <i class="fa fa-times mr-1"></i> Limpar selecao
            </b-button>
            <b-button variant="success" pill size="sm" :disabled="!selecionados.length" @click="baixarSelecionados">
              <i class="fa fa-check-circle mr-1"></i> Baixar selecionados
            </b-button>
          </div>
        </div>
        <b-table
          ref="tabela"
          small
          hover
          responsive
          selectable
          select-mode="multi"
          selected-variant="success"
          :items="titulosFiltrados"
          :fields="fields"
          :per-page="perPage"
          :current-page="currentPage"
          @row-selected="selecionados = $event"
        >
          <template #cell(DOCUMENTO)="data">
            <b-button variant="link" class="p-0 font-weight-bold" @click="editar(data.item)">{{ data.item.DOCUMENTO }}</b-button>
          </template>
          <template #cell(DT_VENC)="data">{{ formatDate(data.item.DT_VENC) }}</template>
          <template #cell(VL_RESTANTE)="data">{{ formatBRL(data.item.VL_RESTANTE) }}</template>
          <template #cell(DIAS_ATRASO)="data">
            <b-badge :variant="data.item.DIAS_ATRASO > 0 ? 'danger' : 'success'" pill>{{ data.item.DIAS_ATRASO || 0 }}</b-badge>
          </template>
          <template #cell(actions)="data">
            <b-button size="sm" variant="soft-success" pill class="btn-icon-only" @click="abrirBaixa(data.item)" v-b-tooltip.hover title="Baixar titulo">
              <i class="fa fa-money"></i>
            </b-button>
          </template>
        </b-table>
        <b-pagination v-model="currentPage" :total-rows="titulosFiltrados.length" :per-page="perPage" align="right" class="mb-0" />
      </section>
    </b-overlay>

    <b-modal ref="modal-baixa" hide-header hide-footer size="lg" centered body-class="p-0">
      <div class="pay-head">
        <div>
          <h5>Baixar titulo</h5>
          <span>{{ baixa.nomeCliente }} | Doc. {{ baixa.documento }} / {{ baixa.parcela }}</span>
        </div>
        <b-button variant="link" class="text-white" @click="$refs['modal-baixa'].hide()"><i class="fa fa-times"></i></b-button>
      </div>
      <div class="p-4">
        <b-row>
          <b-col md="4">
            <label>Tipo</label>
            <b-form-select v-model="baixa.tipoPagamento" :options="tipoPagamentoOpcoes" />
          </b-col>
          <b-col md="4">
            <label>Data</label>
            <b-form-input type="date" v-model="baixa.dataMovimento" />
          </b-col>
          <b-col md="4">
            <label>Valor</label>
            <vue-autonumeric v-model="baixa.valorPago" :options="'Brazilian'" class="form-control text-right" />
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col md="8">
            <label>Historico do caixa</label>
            <historico-caixa-dropdown v-model="baixa.historicoId" tipo="DEBITO" :state="!!baixa.historicoId" />
          </b-col>
          <b-col md="4">
            <label>Conta</label>
            <b-form-select v-model="baixa.contaId" :options="contasOpcoes">
              <template slot="first"><option :value="null">-- Conta --</option></template>
            </b-form-select>
          </b-col>
        </b-row>
        <div class="pay-summary mt-4">
          <div><span>Original</span><strong>{{ formatBRL(baixa.valorDocumento) }}</strong></div>
          <div><span>Aberto</span><strong>{{ formatBRL(baixa.valorAberto) }}</strong></div>
          <div><span>Apos baixa</span><strong>{{ formatBRL(valorRestanteBaixa) }}</strong></div>
        </div>
      </div>
      <div class="pay-foot">
        <b-button variant="outline-secondary" pill @click="$refs['modal-baixa'].hide()">Cancelar</b-button>
        <b-button variant="success" pill :disabled="baixando || !baixa.historicoId || baixa.valorPago <= 0" @click="confirmarBaixa">
          <i class="fa fa-check mr-1"></i> Confirmar pagamento
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Chart from 'chart.js'
import moment from 'moment'
import VueAutonumeric from 'vue-autonumeric'
import HistoricoCaixaDropdown from '@/views/componentes/dropdown/HistoricoCaixaDropdown.vue'

export default {
  name: 'ApagarAnalyticsView',
  components: { VueAutonumeric, HistoricoCaixaDropdown },
  data() {
    return {
      loading: false,
      baixando: false,
      busca: '',
      filtro: { tipo: null, valor: null, label: 'Todos os titulos em aberto' },
      dados: { resumo: {}, fornecedores: [], vencimentoBuckets: [], grupos: [], titulos: [], baixadoHoje: {}, ultimasBaixas: [] },
      charts: { faixas: null, grupos: null },
      selecionados: [],
      perPage: 25,
      currentPage: 1,
      contasOpcoes: [],
      historicoPadrao: null,
      baixaLote: [],
      baixa: this.novaBaixa(),
      tipoPagamentoOpcoes: [
        { value: '0', text: 'Dinheiro/Carteira' },
        { value: '1', text: 'Deposito Bancario' },
        { value: '2', text: 'Boleto' },
        { value: '3', text: 'Cheque' },
        { value: '4', text: 'Cartao de Credito' },
        { value: '6', text: 'PIX' }
      ],
      fields: [
        { key: 'DOCUMENTO', label: 'Documento', sortable: true },
        { key: 'PARCELAS', label: 'Parc.', class: 'text-center', sortable: true },
        { key: 'NOME_FORNECEDOR', label: 'Fornecedor', sortable: true },
        { key: 'NOME_GRUPO', label: 'Grupo', sortable: true },
        { key: 'DT_VENC', label: 'Vencimento', class: 'text-right', sortable: true },
        { key: 'DIAS_ATRASO', label: 'Atraso', class: 'text-center', sortable: true },
        { key: 'VL_RESTANTE', label: 'Aberto', class: 'text-right', sortable: true },
        { key: 'actions', label: '', class: 'text-right' }
      ]
    }
  },
  computed: {
    resumo() { return this.dados.resumo || {} },
    fornecedores() { return this.dados.fornecedores || [] },
    grupos() { return this.dados.grupos || [] },
    baixadoHoje() { return this.dados.baixadoHoje || {} },
    ultimasBaixas() { return this.dados.ultimasBaixas || [] },
    maxFornecedor() { return Math.max(...this.fornecedores.map(i => Number(i.TOTAL_ABERTO) || 0), 1) },
    kpis() {
      return [
        { label: 'Total aberto', value: this.formatBRL(this.resumo.TOTAL_ABERTO), hint: `${this.resumo.TITULOS || 0} titulos`, icon: 'fa fa-briefcase', class: 'blue', filter: null },
        { label: 'Atrasado', value: this.formatBRL(this.resumo.TOTAL_ATRASADO), hint: 'clique para ver vencidos', icon: 'fa fa-warning', class: 'red', filter: { tipo: 'status', valor: 'atrasado', label: 'Titulos atrasados' } },
        { label: 'Vence hoje', value: this.formatBRL(this.resumo.VENCE_HOJE), hint: 'prioridade do dia', icon: 'fa fa-calendar-check-o', class: 'amber', filter: { tipo: 'status', valor: 'hoje', label: 'Vencendo hoje' } },
        { label: 'Prox. 7 dias', value: this.formatBRL(this.resumo.PROX_7), hint: 'pressao imediata', icon: 'fa fa-calendar', class: 'green', filter: { tipo: 'status', valor: '7dias', label: 'Vencimento nos proximos 7 dias' } },
        { label: 'Baixado hoje', value: this.formatBRL(this.baixadoHoje.TOTAL), hint: `${this.baixadoHoje.TITULOS || 0} pagamentos`, icon: 'fa fa-check-circle', class: 'teal', filter: null }
      ]
    },
    titulosFiltrados() {
      const termo = String(this.busca || '').toLowerCase()
      return (this.dados.titulos || []).filter(t => {
        const porFiltro = this.matchFiltro(t)
        const porBusca = !termo || [t.DOCUMENTO, t.PARCELAS, t.NOME_FORNECEDOR, t.NOME_GRUPO].some(v => String(v || '').toLowerCase().includes(termo))
        return porFiltro && porBusca
      })
    },
    totalFiltrado() {
      return this.titulosFiltrados.reduce((s, t) => s + (Number(t.VL_RESTANTE) || 0), 0)
    },
    filtroLabel() {
      return this.filtro.label || 'Todos os titulos em aberto'
    },
    valorRestanteBaixa() {
      return (Number(this.baixa.valorAberto) || 0) - (Number(this.baixa.valorPago) || 0)
    }
  },
  mounted() {
    this.carregar()
  },
  beforeDestroy() {
    this.destruirCharts()
  },
  methods: {
    novaBaixa() {
      return { apagarId: null, documento: '', parcela: '', clienteId: null, nomeCliente: '', dataVencimento: '', dataMovimento: moment().format('YYYY-MM-DD'), tipoPagamento: '0', valorPago: 0, valorPagamentos: 0, valorAjustes: 0, valorDocumento: 0, valorRestante: 0, valorAberto: 0, contaId: null, vendedorId: null, usuario: window.localStorage.getItem('login'), historicoId: null }
    },
    async carregar() {
      this.loading = true
      try {
        const { data } = await this.$http.get('/api/protected/apagar/analytics')
        this.dados = data || this.dados
        await this.carregarContas()
        await this.carregarHistoricoPadrao()
        this.loading = false
        this.$nextTick(this.renderCharts)
      } catch (err) {
        this.loading = false
        this.$bvToast.toast('Nao foi possivel carregar o painel analitico.', { variant: 'danger' })
      }
    },
    async carregarContas() {
      const { data } = await this.$http.get('/api/protected/conta/listar')
      this.contasOpcoes = (data || []).map(c => ({ value: c.CODIGO, text: `${c.CODIGO} - ${c.DESCRICAO}` }))
    },
    async carregarHistoricoPadrao() {
      try {
        const { data } = await this.$http.get('/api/protected/parametro/getHistoricosPadrao')
        this.historicoPadrao = data && data.ID_PLANO_BAIXA_PAGAR
      } catch (err) {}
    },
    renderCharts() {
      this.destruirCharts()
      const buckets = this.dados.vencimentoBuckets || []
      this.charts.faixas = new Chart(this.$refs.chartFaixas.getContext('2d'), {
        type: 'bar',
        data: { labels: buckets.map(i => i.FAIXA), datasets: [{ data: buckets.map(i => Number(i.TOTAL) || 0), backgroundColor: ['#10b981', '#f59e0b', '#fb923c', '#ef4444', '#b91c1c', '#7f1d1d'] }] },
        options: { responsive: true, maintainAspectRatio: false, legend: { display: false }, onClick: (e) => this.chartClick(e, 'faixa'), scales: { yAxes: [{ ticks: { beginAtZero: true, callback: v => this.formatCompact(v) } }] } }
      })
      const grupos = this.grupos.slice(0, 8)
      this.charts.grupos = new Chart(this.$refs.chartGrupos.getContext('2d'), {
        type: 'doughnut',
        data: { labels: grupos.map(i => i.NOME_GRUPO), datasets: [{ data: grupos.map(i => Number(i.TOTAL_ABERTO) || 0), backgroundColor: ['#2563eb', '#0f766e', '#f59e0b', '#ef4444', '#64748b', '#06b6d4', '#84cc16', '#a855f7'] }] },
        options: { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' }, onClick: (e) => this.chartClick(e, 'grupo') }
      })
    },
    chartClick(event, tipo) {
      const chart = tipo === 'faixa' ? this.charts.faixas : this.charts.grupos
      const hit = chart.getElementAtEvent(event)[0]
      if (!hit) return
      const label = chart.data.labels[hit._index]
      if (tipo === 'faixa') this.filtrar('faixa', label, `Faixa: ${label}`)
      if (tipo === 'grupo') this.filtrar('grupo', label, `Grupo: ${label}`)
    },
    destruirCharts() {
      Object.keys(this.charts).forEach(k => { if (this.charts[k]) { this.charts[k].destroy(); this.charts[k] = null } })
    },
    filtrar(tipo, valor, label) {
      this.filtro = { tipo, valor, label }
      this.currentPage = 1
      this.selecionados = []
    },
    aplicarAtalho(filter) {
      if (!filter) return this.limparFiltro()
      this.filtrar(filter.tipo, filter.valor, filter.label)
    },
    limparFiltro() {
      this.filtro = { tipo: null, valor: null, label: 'Todos os titulos em aberto' }
      this.currentPage = 1
    },
    matchFiltro(t) {
      if (!this.filtro.tipo) return true
      if (this.filtro.tipo === 'fornecedor') return Number(t.COD_FORNEC) === Number(this.filtro.valor)
      if (this.filtro.tipo === 'grupo') return t.NOME_GRUPO === this.filtro.valor
      if (this.filtro.tipo === 'faixa') return t.FAIXA === this.filtro.valor
      if (this.filtro.tipo === 'status' && this.filtro.valor === 'atrasado') return Number(t.DIAS_ATRASO) > 0
      if (this.filtro.tipo === 'status' && this.filtro.valor === 'hoje') return moment(t.DT_VENC).isSame(moment(), 'day')
      if (this.filtro.tipo === 'status' && this.filtro.valor === '7dias') return moment(t.DT_VENC).isAfter(moment(), 'day') && moment(t.DT_VENC).isSameOrBefore(moment().add(7, 'days'), 'day')
      return true
    },
    abrirBaixa(titulo) {
      this.baixaLote = [titulo]
      this.baixa = this.novaBaixa()
      this.baixa.apagarId = titulo.SEQUENCIA
      this.baixa.documento = titulo.DOCUMENTO
      this.baixa.parcela = titulo.PARCELAS
      this.baixa.clienteId = titulo.COD_FORNEC
      this.baixa.nomeCliente = titulo.NOME_FORNECEDOR
      this.baixa.dataVencimento = moment(titulo.DT_VENC).format('DD/MM/YYYY')
      this.baixa.valorPago = Number(titulo.VL_RESTANTE) || 0
      this.baixa.valorAberto = Number(titulo.VL_RESTANTE) || 0
      this.baixa.valorDocumento = Number(titulo.VL_DOC) || this.baixa.valorAberto
      this.baixa.valorRestante = 0
      this.baixa.historicoId = this.historicoPadrao || null
      this.$refs['modal-baixa'].show()
    },
    baixarSelecionados() {
      if (this.selecionados.length === 1) return this.abrirBaixa(this.selecionados[0])
      this.baixaLote = this.selecionados.slice()
      const total = this.baixaLote.reduce((s, t) => s + (Number(t.VL_RESTANTE) || 0), 0)
      this.baixa = this.novaBaixa()
      this.baixa.documento = `${this.baixaLote.length} titulos selecionados`
      this.baixa.parcela = 'lote'
      this.baixa.nomeCliente = 'Baixa em lote'
      this.baixa.valorPago = total
      this.baixa.valorAberto = total
      this.baixa.valorDocumento = total
      this.baixa.historicoId = this.historicoPadrao || null
      this.$refs['modal-baixa'].show()
    },
    async confirmarBaixa() {
      this.baixando = true
      try {
        const titulos = this.baixaLote.length ? this.baixaLote : []
        for (const titulo of titulos) {
          const payload = Object.assign({}, this.baixa, {
            apagarId: titulo.SEQUENCIA,
            documento: titulo.DOCUMENTO,
            parcela: titulo.PARCELAS,
            clienteId: titulo.COD_FORNEC,
            nomeCliente: titulo.NOME_FORNECEDOR,
            dataVencimento: moment(titulo.DT_VENC).format('DD/MM/YYYY'),
            valorPago: Number(titulo.VL_RESTANTE) || 0,
            valorAberto: Number(titulo.VL_RESTANTE) || 0,
            valorDocumento: Number(titulo.VL_DOC) || Number(titulo.VL_RESTANTE) || 0,
            valorRestante: 0
          })
          await this.$http.post('/api/protected/apagarbaixa/baixar', payload)
        }
        this.$bvToast.toast(titulos.length > 1 ? 'Titulos baixados com sucesso.' : 'Documento baixado com sucesso.', { variant: 'success' })
        this.$refs['modal-baixa'].hide()
        this.selecionados = []
        await this.carregar()
      } catch (err) {
        this.$bvToast.toast('Falha ao baixar o documento.', { variant: 'danger' })
      } finally {
        this.baixando = false
      }
    },
    editar(titulo) {
      this.$router.push(`/movimentos/contasapagar/edit/${titulo.SEQUENCIA}`)
    },
    percent(v, max) { return Math.max(3, Math.min(100, ((Number(v) || 0) / (Number(max) || 1)) * 100)) },
    formatBRL(v) { return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
    formatDate(v) { return v ? moment(v).format('DD/MM/YYYY') : '-' },
    formatCompact(v) { const n = Number(v) || 0; return n >= 1000000 ? `${(n / 1000000).toFixed(1)} mi` : n >= 1000 ? `${(n / 1000).toFixed(0)} mil` : n.toFixed(0) }
  }
}
</script>

<style scoped>
.apagar-workbench { min-height: calc(100vh - 110px); background: #eef2f7; padding: 18px; }
.workbench-header { display: flex; justify-content: space-between; gap: 16px; align-items: center; padding: 18px 20px; background: linear-gradient(135deg, #111827, #164e63 58%, #0f766e); color: #fff; border-radius: 8px; box-shadow: 0 12px 26px rgba(15,23,42,.18); }
.workbench-header > div:first-child { display: flex; align-items: center; gap: 12px; }
.workbench-header h3 { margin: 0; font-weight: 800; text-transform: uppercase; }
.workbench-header span { color: rgba(255,255,255,.72); }
.back-btn { color: #fff; font-size: 1.25rem; }
.header-actions { display: flex; gap: 10px; align-items: center; min-width: 420px; }
.header-actions .form-control { border: 0; border-radius: 999px; }
.kpi-grid { display: grid; grid-template-columns: repeat(5, minmax(150px, 1fr)); gap: 14px; margin: 16px 0; }
.kpi-card { position: relative; min-height: 118px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 15px; text-align: left; box-shadow: 0 8px 18px rgba(15,23,42,.06); cursor: pointer; }
.kpi-card small, .kpi-card span { display: block; color: #64748b; font-weight: 700; }
.kpi-card small { text-transform: uppercase; font-size: .68rem; }
.kpi-card strong { display: block; color: #0f172a; font-size: 1.15rem; margin-top: 8px; }
.kpi-card i { position: absolute; right: 14px; bottom: 12px; font-size: 1.6rem; opacity: .18; }
.kpi-card.blue { border-top: 3px solid #2563eb; } .kpi-card.red { border-top: 3px solid #ef4444; } .kpi-card.amber { border-top: 3px solid #f59e0b; } .kpi-card.green { border-top: 3px solid #10b981; } .kpi-card.teal { border-top: 3px solid #0f766e; }
.workbench-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 14px; }
.panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; box-shadow: 0 10px 22px rgba(15,23,42,.06); }
.panel-wide { grid-row: span 2; }
.panel-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-bottom: 10px; margin-bottom: 10px; border-bottom: 1px solid #edf2f7; }
.panel-head strong { display: block; color: #111827; font-size: .95rem; }
.panel-head span { display: block; color: #64748b; font-size: .76rem; }
.supplier-board { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; max-height: 568px; overflow: auto; padding-right: 4px; }
.supplier-card { border: 1px solid #e2e8f0; background: #f8fafc; border-radius: 8px; padding: 12px; text-align: left; cursor: pointer; }
.supplier-card.active, .supplier-card:hover { border-color: #0891b2; background: #ecfeff; }
.supplier-card span, .supplier-card strong, .supplier-card em { display: block; }
.supplier-card span { color: #0f172a; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.supplier-card strong { margin: 6px 0 2px; }
.supplier-card em { color: #64748b; font-style: normal; font-size: .74rem; margin-bottom: 8px; }
.chart-wrap { height: 260px; }
.today-paid strong { display: block; color: #0f766e; font-size: 1.8rem; }
.today-paid span { color: #64748b; }
.recent-list { margin-top: 12px; }
.recent-row { display: flex; justify-content: space-between; gap: 8px; border-top: 1px solid #f1f5f9; padding: 8px 0; }
.recent-row strong, .recent-row span { display: block; }
.recent-row strong { color: #0f172a; font-size: .78rem; }
.recent-row span { color: #64748b; font-size: .72rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 170px; }
.recent-row em { color: #0f766e; font-style: normal; font-weight: 800; }
.table-panel { margin-top: 14px; }
.pay-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; background: linear-gradient(135deg, #0891b2, #0f766e); color: #fff; }
.pay-head h5 { margin: 0; font-weight: 800; }
.pay-head span { color: rgba(255,255,255,.75); }
.pay-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.pay-summary div { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; text-align: center; }
.pay-summary span, .pay-summary strong { display: block; }
.pay-summary span { color: #64748b; font-size: .74rem; font-weight: 800; text-transform: uppercase; }
.pay-foot { display: flex; justify-content: space-between; padding: 14px 20px; background: #f8fafc; border-top: 1px solid #e2e8f0; }
@media (max-width: 1200px) { .workbench-grid, .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .panel-wide { grid-row: auto; } }
@media (max-width: 768px) { .workbench-header, .header-actions { flex-direction: column; align-items: stretch; min-width: 0; } .supplier-board, .workbench-grid, .kpi-grid, .pay-summary { grid-template-columns: 1fr; } }
</style>



// WEBPACK FOOTER //
// src/views/movimentos/contasapagar/ApagarAnalyticsView.vue